import { useShow } from "@refinedev/core";
import { DateField } from "@refinedev/antd";
import { Typography, Tag, Table, Card, Button, Spin } from "antd";
import type { TableProps } from 'antd';
import { 
    PrinterOutlined, 
    ShoppingCartOutlined,
    CheckCircleOutlined,
} from "@ant-design/icons";
import React from "react";
import { useParams, Link } from "react-router-dom";
import { PageHeader } from "../../components/ui";

const { Text } = Typography;

interface VendorAddress {
    id: number;
    houseNo: string;
    street: string;
    city: string;
    province: string;
    country: string;
    isPrimary: boolean;
}

interface PurchaseItem {
    id: number;
    inventoryItemId: number;
    quantity: number;
    purchasePrice: number;
    inventoryItem?: {
        itemCode: string;
        itemName: string;
        itemClassificationCode: string;
    };
}

export const PurchaseShow = () => {
    const { id } = useParams();
    const { queryResult } = useShow({
        resource: "purchases",
    });
    const { data, isLoading } = queryResult;
    const record = data?.data;

    const [documentLoading, setDocumentLoading] = React.useState(false);

    const handleViewDocument = () => {
        if (record?.documentUrl) {
            setDocumentLoading(true);
            window.open(record.documentUrl, '_blank');
            setTimeout(() => setDocumentLoading(false), 1000);
        }
    };

    const formatCurrency = (value: number | undefined | null, precision: number = 2): string => {
        if (value === undefined || value === null) return '0.00';
        return value.toFixed(precision);
    };

    const getStatusTagColor = (status: string | undefined): string => {
        switch (status) {
            case "Completed": return "green";
            case "Pending": return "orange";
            case "Cancelled": return "red";
            case "Draft": return "blue";
            default: return "default";
        }
    };

    const primaryContact = record?.vendor?.vendorContacts?.find((contact: any) => contact.isPrimary) ||
        (record?.vendor?.vendorContacts?.length > 0 ? record.vendor.vendorContacts[0] : null);

    const primaryAddress = record?.vendor?.vendorAddresses?.find((address: any) => address.isPrimary) ||
        (record?.vendor?.vendorAddresses?.length > 0 ? record.vendor.vendorAddresses[0] : null);

    const formatAddress = (address: VendorAddress | null): string => {
        if (!address) return '-';
        return `${address.houseNo} ${address.street}, ${address.city}, ${address.province}, ${address.country}`;
    };

    const itemColumns: TableProps<PurchaseItem>['columns'] = [
        {
            title: 'Item Code',
            dataIndex: ['inventoryItem', 'itemCode'],
            key: 'itemCode',
            render: (value, item) => value || `ID: ${item.inventoryItemId}`
        },
        {
            title: 'Item Name',
            dataIndex: ['inventoryItem', 'itemName'],
            key: 'itemName',
            render: (value) => value || '-'
        },
        {
            title: 'Classification',
            dataIndex: ['inventoryItem', 'itemClassificationCode'],
            key: 'itemClassificationCode',
            render: (value) => value || '-'
        },
        {
            title: 'Quantity',
            dataIndex: 'quantity',
            key: 'quantity',
            align: 'right',
            render: (value: number) => formatCurrency(value, 2)
        },
        {
            title: 'Purchase Price',
            dataIndex: 'purchasePrice',
            key: 'purchasePrice',
            align: 'right',
            render: (value: number) => `$${formatCurrency(value, 2)}`
        },
        {
            title: 'Total',
            key: 'total',
            align: 'right',
            render: (_, item: PurchaseItem) => (
                <Text strong>${formatCurrency(item.quantity * item.purchasePrice, 2)}</Text>
            )
        }
    ];

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-20">
                <Spin size="large" />
            </div>
        );
    }

    return (
        <div className="show-page-wrapper">
            <PageHeader
                title={`Purchase #${record?.orderNumber || ''}`}
                titleIcon={<ShoppingCartOutlined />}
                breadcrumbs={[
                    { title: "Purchases", href: "/purchases" },
                    { title: "Show Purchase" },
                ]}
                actions={[
                    ...(record?.documentUrl && record?.status === "Completed" ? [{
                        label: "Print",
                        icon: <PrinterOutlined />,
                        onClick: handleViewDocument,
                        variant: "outlined" as const,
                    }] : []),
                ]}
            >
                {/* Purchase-specific mini stat cards */}
                <div className="mini-stats-row">
                    <div className="mini-stat-card">
                        <div className="mini-stat-label">Total Amount</div>
                        <div className="mini-stat-value success">${formatCurrency(record?.totalAmount)}</div>
                    </div>
                    <div className="mini-stat-card">
                        <div className="mini-stat-label">Status</div>
                        <Tag color={getStatusTagColor(record?.status)} style={{ margin: 0 }}>
                            {record?.status}
                        </Tag>
                    </div>
                    <div className="mini-stat-card">
                        <div className="mini-stat-label">Currency</div>
                        <div className="mini-stat-value">{record?.currencyTypeCode || 'USD'}</div>
                    </div>
                </div>
            </PageHeader>

            <div className="space-y-4">
                {/* Purchase Summary Card */}
                <div className="info-card">
                    <div className="info-card-header">
                        <h3 className="info-card-title">Purchase Summary</h3>
                    </div>
                    <div className="info-card-body">
                        <div className="info-grid">
                            <div className="info-item">
                                <span className="info-label">Order Number:</span>
                                <span className="info-value">{record?.orderNumber || '-'}</span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Transaction Date:</span>
                                <span className="info-value">
                                    <DateField value={record?.transactionDate} format="YYYY-MM-DD" />
                                </span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Status:</span>
                                <Tag color={getStatusTagColor(record?.status)}>{record?.status}</Tag>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Created At:</span>
                                <span className="info-value">
                                    <DateField value={record?.createdAt} format="YYYY-MM-DD HH:mm:ss" />
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Vendor Details Card */}
                {record?.vendor && (
                    <div className="info-card">
                        <div className="info-card-header">
                            <h3 className="info-card-title">Vendor Details</h3>
                        </div>
                        <div className="info-card-body">
                            <div className="info-grid">
                                <div className="info-item">
                                    <span className="info-label">Name:</span>
                                    <span className="info-value">
                                        <Link to={`/vendors/show/${record.vendorId}`}>
                                            <Text strong style={{ color: 'var(--color-primary)' }}>{record.vendor.name}</Text>
                                        </Link>
                                    </span>
                                </div>
                                <div className="info-item">
                                    <span className="info-label">Trade Name:</span>
                                    <span className="info-value">{record.vendor.tradeName || '-'}</span>
                                </div>
                                <div className="info-item">
                                    <span className="info-label">TPIN:</span>
                                    <span className="info-value">{record.vendor.tpin || '-'}</span>
                                </div>
                                <div className="info-item">
                                    <span className="info-label">Phone:</span>
                                    <span className="info-value">{primaryContact?.phoneNumber || '-'}</span>
                                </div>
                                <div className="info-item">
                                    <span className="info-label">Email:</span>
                                    <span className="info-value">{primaryContact?.email || '-'}</span>
                                </div>
                                <div className="info-item">
                                    <span className="info-label">Bank:</span>
                                    <span className="info-value">{record.vendor.bankName || '-'}</span>
                                </div>
                                <div className="info-item full-width">
                                    <span className="info-label">Address:</span>
                                    <span className="info-value">{formatAddress(primaryAddress)}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Financial Details Card */}
                <div className="info-card">
                    <div className="info-card-header">
                        <h3 className="info-card-title">Financial Details</h3>
                    </div>
                    <div className="info-card-body">
                        <div className="info-grid">
                            <div className="info-item">
                                <span className="info-label">Currency:</span>
                                <span className="info-value">{record?.currencyTypeCode || '-'}</span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Exchange Rate:</span>
                                <span className="info-value">{formatCurrency(record?.exchangeRate, 4)}</span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Subtotal:</span>
                                <span className="info-value">${formatCurrency(record?.subTotal)}</span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Total Tax:</span>
                                <span className="info-value">${formatCurrency(record?.totalTax)}</span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Total Amount:</span>
                                <span className="info-value" style={{ fontSize: '18px', fontWeight: 700, color: 'var(--color-success)' }}>
                                    ${formatCurrency(record?.totalAmount)}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Purchase Items Card */}
                <Card title="Purchase Items" className="data-table-card">
                    <Table
                        dataSource={record?.items || []}
                        columns={itemColumns}
                        rowKey="id"
                        pagination={false}
                        summary={(pageData) => {
                            let totalItems = 0;
                            let totalAmount = 0;

                            pageData.forEach((item: PurchaseItem) => {
                                totalItems += item.quantity;
                                totalAmount += item.quantity * item.purchasePrice;
                            });

                            return (
                                <Table.Summary fixed>
                                    <Table.Summary.Row>
                                        <Table.Summary.Cell index={0} colSpan={3}>
                                            <Text strong>Totals:</Text>
                                        </Table.Summary.Cell>
                                        <Table.Summary.Cell index={3} align="right">
                                            <Text strong>{formatCurrency(totalItems, 2)}</Text>
                                        </Table.Summary.Cell>
                                        <Table.Summary.Cell index={4} align="right">
                                            <Text></Text>
                                        </Table.Summary.Cell>
                                        <Table.Summary.Cell index={5} align="right">
                                            <Text strong style={{ fontSize: '16px', color: 'var(--color-success)' }}>
                                                ${formatCurrency(totalAmount, 2)}
                                            </Text>
                                        </Table.Summary.Cell>
                                    </Table.Summary.Row>
                                </Table.Summary>
                            );
                        }}
                    />
                </Card>
            </div>
        </div>
    );
};
